import { RetailType } from "@/custom-types/Dispensary";
import { getRetailerInfo } from "@/utils/getRetailerInfo";
import { retailCopyFormatter } from "@/utils/retailCopyFormatter";

type MetaData = {
  description: string;
  image: string;
  title: string;
};

// --- Helpers ---

const getPageImage = (coverPhotoUrl: string, logoUrl: string): string =>
  coverPhotoUrl || logoUrl;

const getCityStateCombo = (city: string, state: string): string =>
  state === "DC" && city.includes("DC") ? city : `${city}, ${state}`;

const getRetailerCopy = (countryCode: string, retailType: RetailType): string =>
  retailCopyFormatter(
    getRetailerInfo(countryCode, retailType).copy,
    "capitalize",
  );

// --- CBD Store Metadata ---

const cbdStoreDealsMetaData = (
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
): MetaData => ({
  description: `Find the best deals and discounts on CBD or Hemp products like flower, edibles, and topicals from ${name}.`,
  image: getPageImage(coverPhotoUrl, logoUrl),
  title: `${name} Deals & Discounts | Leafly`,
});

const cbdStoreMainMetaData = (
  city: string,
  countryCode: string,
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
  state: string,
): MetaData => ({
  description: `Explore the ${name} menu on Leafly. See what CBD or hemp products are available, compare prices, and read product reviews.`,
  image: getPageImage(coverPhotoUrl, logoUrl),
  title: `${name} | ${getRetailerCopy(countryCode, RetailType.CbdStore)} in ${getCityStateCombo(city, state)} | Leafly`,
});

const cbdStoreMenuMetaData = (
  countryCode: string,
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
): MetaData => ({
  description: `Explore the ${name} menu on Leafly. See what CBD or hemp products are available, compare prices, and read product reviews.`,
  image: getPageImage(coverPhotoUrl, logoUrl),
  title: `${name} Hemp ${getRetailerCopy(countryCode, RetailType.CbdStore)} Menu | Leafly`,
});

// --- Dispensary Metadata ---

const dispensaryDealsMetaData = (
  countryCode: string,
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
): MetaData => ({
  description:
    countryCode !== "CA"
      ? `Find the best dispensary weed deals and discounts on flower, dabs, carts, and edibles at ${name}.`
      : `Find the best licensed dispensary weed deals and discounts on flower, dabs, carts, and edibles at ${name}.`,
  image: getPageImage(coverPhotoUrl, logoUrl),
  title: `${name} Deals & Discounts | Leafly`,
});

const dispensaryMainMetaData = (
  city: string,
  countryCode: string,
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
  state: string,
): MetaData => ({
  description: `Explore the ${name} menu on Leafly. Find out what cannabis and CBD products are available, read reviews, and find just what you’re looking for.`,
  image: getPageImage(coverPhotoUrl, logoUrl),
  title:
    countryCode !== "CA"
      ? `${name} | Dispensary Menu, Reviews & Photos`
      : `${name} | ${getCityStateCombo(city, state)} Dispensary | Leafly`,
});

const dispensaryMenuMetaData = (
  countryCode: string,
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
): MetaData => ({
  description: `Explore the ${name} menu on Leafly. Find out what cannabis and CBD products are available, read reviews, and find just what you’re looking for.`,
  image: getPageImage(coverPhotoUrl, logoUrl),
  title:
    countryCode !== "CA"
      ? `${name} | Dispensary Menu, Reviews & Photos`
      : `${name} ${getRetailerCopy(countryCode, RetailType.Dispensary)} Menu | Leafly`,
});

// --- Metadata Generators ---

export const retailerClinicMetaData = (
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
): MetaData => ({
  description: `Get or renew your medical marijuana card at ${name}. Get business hours, find contact info, book an appointment, and get directions with Leafly.`,
  image: getPageImage(coverPhotoUrl, logoUrl),
  title: `${name} | Medical Marijuana Cards`,
});

export const retailerDealsMetaData = (
  countryCode: string,
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
  retailType: RetailType,
): MetaData => {
  switch (retailType) {
    case RetailType.CbdStore:
      return cbdStoreDealsMetaData(coverPhotoUrl, logoUrl, name);
    case RetailType.Dispensary:
      return dispensaryDealsMetaData(countryCode, coverPhotoUrl, logoUrl, name);
    default:
      throw new Error(`Unsupported retailType: ${retailType}`);
  }
};

export const retailerMainMetaData = (
  city: string,
  countryCode: string,
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
  retailType: RetailType,
  state: string,
): MetaData => {
  switch (retailType) {
    case RetailType.CbdStore:
      return cbdStoreMainMetaData(
        city,
        countryCode,
        coverPhotoUrl,
        logoUrl,
        name,
        state,
      );
    case RetailType.Dispensary:
      return dispensaryMainMetaData(
        city,
        countryCode,
        coverPhotoUrl,
        logoUrl,
        name,
        state,
      );
    default:
      throw new Error(`Unsupported retailType: ${retailType}`);
  }
};

export const retailerMenuMetaData = (
  countryCode: string,
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
  retailType: RetailType,
): MetaData => {
  switch (retailType) {
    case RetailType.CbdStore:
      return cbdStoreMenuMetaData(countryCode, coverPhotoUrl, logoUrl, name);
    case RetailType.Dispensary:
      return dispensaryMenuMetaData(countryCode, coverPhotoUrl, logoUrl, name);
    default:
      throw new Error(`Unsupported retailType: ${retailType}`);
  }
};

export const retailerPdpMetaData = (
  coverPhotoUrl: string,
  brandName: string,
  logoUrl: string,
  menuItemName: string,
  name: string,
): MetaData => {
  const brandClause = brandName ? ` by ${brandName}` : "";
  const brandNameClause = brandName ? ` ${brandName}` : "";
  return {
    description: `Shop at ${name} for ${menuItemName}${brandClause}. Browse the dispensary menu or find more${brandNameClause} products at Leafly.`,
    image: getPageImage(coverPhotoUrl, logoUrl),
    title: `${menuItemName}${brandClause} at ${name}`,
  };
};

export const retailerReviewsMetaData = (
  coverPhotoUrl: string,
  logoUrl: string,
  name: string,
  pageNumber: number,
): MetaData => {
  const descriptionPageText = pageNumber > 1 ? `. Page ${pageNumber}` : "";
  const titlePageText = pageNumber > 1 ? ` - Page ${pageNumber}` : "";
  return {
    description: `Read customer reviews for ${name} including scores for quality, service, and atmosphere. Been there? Leave a dispensary review of your own${descriptionPageText}.`,
    image: getPageImage(coverPhotoUrl, logoUrl),
    title: `${name} Customer Reviews from Leafly${titlePageText}`,
  };
};
